import { INJ_DENOM, NBZ_DENOM, SHU_DENOM, SOL_DENOM, USDC_NEW_DENOM } from '@/constants/constants'
import { TokenMoney } from '@ninjablaze/sdk-ts'

import { create } from 'zustand'
import { createComputed } from 'zustand-computed'
import { devtools } from 'zustand/middleware'

export const currenciesSettings = {
  minValue: {
    [INJ_DENOM]: 0.01,
    [NBZ_DENOM]: 1,
    [SHU_DENOM]: 1,
    [USDC_NEW_DENOM]: 0.1,
    [SOL_DENOM]: 0.001,
  },
  maxValue: {
    [INJ_DENOM]: 1,
    [NBZ_DENOM]: 1_000,
    [SHU_DENOM]: 100,
    [USDC_NEW_DENOM]: 10,
    [SOL_DENOM]: 1,
  },
  maxWin: {
    [INJ_DENOM]: 50,
    [NBZ_DENOM]: 50_000,
    [SHU_DENOM]: 10_000,
    [USDC_NEW_DENOM]: 500,
    [SOL_DENOM]: 25,
  },
}

type TransactionBatchState = {
  tick: (sendTransaction: any) => void
  addTx: (tx: { msg: any }, sendTransaction: any) => void
  txsPending: boolean
  txId: number
  txs: any[]
}

type Game = {
  id: string
  bet: string
  denom: string
  mines: string
  rtp: string
  maxCells: string
  maxWin: string
} | null

type FirebaseGameState = {
  bet: string
  cells: string
  denom: string
  mines: string
  payable: string
  status: string
  rounds: Array<{ mines: string; user_choice: string; result: string }>
} | null

type GameState = {
  // TODO: Game, GameState and FirebaseGameState?
  bet: string
  denom: string
  cells: Array<number>
  mines: Array<number>
  payable: string
  status: string
  rounds: Array<{ mines: string; user_choice: string; result: string }>
} | null

type MinesState = {
  // TODO: Game, GameState and LocalGameState?
  contractConfig: { rtp: string; maxBet: string; maxWin: string }
  initialized: boolean
  game: Game

  isFormDirty: boolean
  setFormDirty: (value: boolean) => void
  denom: string
  setDenom: (denom: string) => void

  isButtonActionLoading: boolean
  setButtonActionLoading: (value: boolean) => void

  setInitialized: (value: boolean) => void
  setGame: (game: Game) => void

  localGameState: {
    cells: number[]
  }
  gameState: GameState

  setGameState: (state: FirebaseGameState) => void

  openCell: (index: number) => void
  removeCell: (index: number) => void

  transactionLogs: any[]

  winPanel?: {
    payout?: TokenMoney
    multiplier?: string
  }
} & TransactionBatchState

type ComputedStore = {
  isGameActive: boolean
  isCanWithdraw: boolean
}
const computed = createComputed(
  (state: MinesState): ComputedStore => ({
    isGameActive: !!state.game?.id && state.gameState?.status == undefined,
    isCanWithdraw: state.gameState?.status === 'win',
  })
)

export const useMinesStore = create<MinesState>()(
  devtools(
    computed((set, get) => ({
      contractConfig: { rtp: '0.96', maxBet: '1000000000000000000', maxWin: '25000000000000000000' },
      initialized: false,
      game: null,
      isButtonActionLoading: false,
      txsPending: false,
      txId: 0,
      gameState: null,
      localGameState: { cells: [] },

      denom: USDC_NEW_DENOM,
      setDenom: (denom) => {
        set({ denom })
      },
      isFormDirty: false,
      setFormDirty: (value) => {
        set({ isFormDirty: value })
      },

      setButtonActionLoading: (value) => {
        set({ isButtonActionLoading: value })
      },
      setInitialized: (value) => {
        set({ initialized: value })
      },
      setGameState: (state) => {
        // TODO
        set({
          initialized: true,
          isFormDirty: false,
          gameState: state && {
            ...state,
            cells: state.cells ? state.cells.split(',').map(Number) : [],
            mines: state.mines ? state.mines.split(',').map(Number) : [],
          },
        })

        if (state) {
          const currentState = get().localGameState
          if (currentState.cells.length === 0) {
            set({
              localGameState: { cells: state.cells.split(',').map(Number) },
            })
          }
        }

        if (state?.status === 'win') {
          set({
            game: null,
            gameState: null,
            localGameState: { cells: [] },
          })
        }

        if (state?.status === 'lose') {
          set({ txId: 0, txs: [], game: null, txsPending: false })
        }
      },

      setGame(game) {
        set({
          game: game,
          gameState: null,
          localGameState: { cells: [] },
          isButtonActionLoading: false,
        })
        if (game) {
          set({ denom: game.denom })
        }
      },

      openCell(index: number) {
        set((state) => ({
          localGameState: {
            cells: [...state.localGameState.cells, index],
          },
        }))
      },

      removeCell(index: number) {
        set((state) => ({
          localGameState: {
            cells: state.localGameState.cells.filter((cell) => cell !== index),
          },
        }))
      },

      addTx: async (tx: { msg: any }, sendTransaction: any) => {
        return await new Promise((resolve, reject) => {
          set((state) => ({ txs: [...state.txs, { id: state.txId++, tx, resolve, reject }], txId: state.txId++ }))

          get().tick(sendTransaction)
        })
      },
      tick: async (sendTransaction: any) => {
        const results = get()
        if (results.txsPending) {
          return
        }

        const txs = [...results.txs]
        if (txs.length === 0) {
          return
        }

        const msg = txs[0].tx.msg
        msg.params.msg.next_round.cells = txs.flatMap((tx) => tx?.tx?.msg?.params?.msg?.next_round?.cells)

        set({ txsPending: true })

        try {
          const result = await sendTransaction(msg)

          txs.forEach((tx) => {
            tx.resolve(result)
          })
        } catch (error) {
          txs.forEach((tx) => {
            tx.reject(error)
          })
        } finally {
          set({ txsPending: false, txs: get().txs.filter((tx) => !txs.map((z) => z.id).includes(tx.id)) })
          get().tick(sendTransaction)
        }
      },
      txs: [],

      transactionLogs: [],

      addTransactionLogs(value: any, id?: number) {
        const state = get()

        const logs = state.transactionLogs
        if (!id || !logs[id]) {
          const t = [...state.transactionLogs]
          id = t.push(value!)

          set({
            transactionLogs: t,
          })

          return id
        }

        logs[id] = {
          ...logs[id],
          ...value,
        }

        return id
      },
    }))
  )
)
